/**
 * Self-serve signup API.
 *
 * Two kinds of caller:
 * - **The lookup is anonymous**, so it bypasses the shared api client the same way `invitations`
 *   does: the coach has no account yet, and a token must not be sent even when a Firebase user
 *   happens to be signed in.
 * - **Create, checkout and status use the shared client**, which attaches the current Firebase ID
 *   token. Create is the one call made with a Firebase account that has no RosterPilot user yet;
 *   the API gives that path a narrow identity rather than a 401.
 *
 * Every refusal is thrown as a `SignupError` whose `code` comes from the response body, never from
 * the message text, so pages branch on the server's own vocabulary.
 */

import { ApiError, get, post } from './api';
import type {
  CreateSignupRequest,
  CreateSignupResponse,
  SignupCheckoutResponse,
  SignupErrorCode,
  SignupLookupRequest,
  SignupLookupResponse,
  SignupSchool,
  SignupSport,
  SignupStatusResponse,
} from '../types/signup';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const LOOKUP_URL = `${API_BASE_URL}/api/public/signup/lookup`;

export const RATE_LIMITED_MESSAGE = 'Too many attempts, try again later.';
export const NETWORK_MESSAGE =
  'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';

const KNOWN_CODES: ReadonlySet<string> = new Set<SignupErrorCode>([
  'invalid_email',
  'invalid_name',
  'invalid_sport',
  'unverified_domain',
  'email_not_verified',
  'already_exists',
  'email_in_use',
  'already_registered',
  'unavailable',
  'not_awaiting_payment',
  'not_self_serve',
  'forbidden',
  'payment_provider_error',
  'payment_pending',
  'not_found',
]);

/** A refused signup call. `message` is always displayable. */
export class SignupError extends Error {
  readonly code: SignupErrorCode;
  readonly status: number;

  constructor(code: SignupErrorCode, message: string, status: number) {
    super(message);
    this.name = 'SignupError';
    this.code = code;
    this.status = status;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/** Status + parsed body to a `SignupError`. The body's `code` wins; the status is the fallback. */
const toSignupError = (status: number, body: unknown): SignupError => {
  const record = (body ?? {}) as { code?: unknown; message?: unknown };
  const bodyCode = typeof record.code === 'string' ? record.code : null;
  const message = text(record.message);

  if (status === 429) {
    // The rate limiter answers with an empty body.
    return new SignupError('rate_limited', RATE_LIMITED_MESSAGE, status);
  }

  if (bodyCode && KNOWN_CODES.has(bodyCode)) {
    return new SignupError(bodyCode as SignupErrorCode, message ?? UNKNOWN_MESSAGE, status);
  }

  if (status === 401) {
    return new SignupError(
      'unauthorized',
      message ?? 'Your session has expired. Sign in again to continue.',
      status
    );
  }

  return new SignupError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

/** Wraps a shared-client failure. A status of 0 is the client's own "never reached the server". */
const fromApiError = (error: unknown): SignupError => {
  if (error instanceof SignupError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) {
      return new SignupError('network', NETWORK_MESSAGE, 0);
    }
    return toSignupError(error.statusCode, error.response);
  }
  return new SignupError('unknown', UNKNOWN_MESSAGE, 0);
};

const isLookupStatus = (value: unknown): value is SignupLookupResponse['status'] =>
  value === 'eligible' || value === 'unverified' || value === 'unavailable';

const isSport = (value: unknown): value is SignupSport =>
  value === 'baseball' || value === 'softball';

/**
 * A lookup answer read through guards rather than cast, or null when it is not one this build
 * understands. Web and the API deploy separately, and the same shape is read back from localStorage
 * (`signupStorage`), so a status this build does not know must not render as an eligible school with
 * no price, and a sport it does not know is dropped rather than offered.
 */
export const parseLookupResponse = (value: unknown): SignupLookupResponse | null => {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (!isLookupStatus(record.status)) return null;

  return {
    status: record.status,
    school:
      typeof record.school === 'object' && record.school !== null
        ? (record.school as SignupSchool)
        : null,
    sports: Array.isArray(record.sports) ? record.sports.filter(isSport) : [],
    annualPriceCents:
      typeof record.annualPriceCents === 'number' ? record.annualPriceCents : null,
    currency: text(record.currency) ?? 'usd',
    message: text(record.message),
  };
};

/**
 * What a school email address would buy: the school and the yearly price.
 *
 * Anonymous. The body carries the email and nothing else. It does not say whether the school's
 * program already exists (that would disclose the customer list): `sports` lists every sport the
 * school offers, and a taken program is refused by `createSignup` with `already_exists`.
 */
export const lookupSignup = async (email: string): Promise<SignupLookupResponse> => {
  const body: SignupLookupRequest = { email };
  let response: Response;

  try {
    response = await fetch(LOOKUP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SignupError('network', NETWORK_MESSAGE, 0);
  }

  const parsed: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw toSignupError(response.status, parsed);
  }

  const lookup = parseLookupResponse(parsed);
  if (!lookup) {
    throw new SignupError('unknown', UNKNOWN_MESSAGE, response.status);
  }

  return lookup;
};

/**
 * Creates the program, locked until paid, for the signed-in Firebase account.
 *
 * Built field by field so the payload can never grow an email, school, tier or price.
 */
export const createSignup = async (
  request: CreateSignupRequest
): Promise<CreateSignupResponse> => {
  try {
    return await post<CreateSignupResponse>('/api/public/signup', {
      firstName: request.firstName,
      lastName: request.lastName,
      sport: request.sport,
    });
  } catch (error) {
    throw fromApiError(error);
  }
};

/** Starts Stripe Checkout for the signed-in admin's own locked program. */
export const startSignupCheckout = async (): Promise<SignupCheckoutResponse> => {
  let result: SignupCheckoutResponse;

  try {
    result = await post<SignupCheckoutResponse>('/api/signup/checkout');
  } catch (error) {
    throw fromApiError(error);
  }

  if (!text(result?.checkoutUrl)) {
    throw new SignupError('payment_provider_error', UNKNOWN_MESSAGE, 200);
  }

  return result;
};

/** The signed-in admin's own signup state. */
export const getSignupStatus = async (): Promise<SignupStatusResponse> => {
  let status: SignupStatusResponse;
  try {
    status = await get<SignupStatusResponse>('/api/signup/status');
  } catch (error) {
    throw fromApiError(error);
  }
  // Only an explicit true holds the page on "processing": an older API that does not send the
  // field must not strand a coach who has not paid.
  return { ...status, paymentPending: status?.paymentPending === true };
};
